/* ------------------------------------------------------------------
  File: Modal.jsx
  Author: Engr. Shaffa K. Kokro Jr. - CEO, Accend Holdings Ltd.
  Last Modified: 2026-05-18
------------------------------------------------------------------ */

// Purpose:
//   - Provides a reusable modal/dialog component for the Accend Holdings website.
//   - Supports accessible dialog behavior, overlay close, escape-key close,
//     body scroll locking, size variants, placement variants, visual variants,
//     header, body, footer, and optional close button.
//   - Uses the acc-modal namespace to avoid conflicts with Bootstrap.
//   - Suitable for confirmations, quick views, forms, media previews,
//     newsletter signup, side drawers, and focused content overlays.

/**
 * Modal
 *
 * A reusable accessible modal/dialog component.
 *
 * @param {boolean} isOpen
 * Controls whether the modal is visible.
 *
 * @param {Function} onClose
 * Function called when the modal should close.
 *
 * @param {string} title
 * Optional modal title.
 *
 * @param {string} description
 * Optional modal description displayed below the title.
 *
 * @param {React.ReactNode} children
 * Main modal body content.
 *
 * @param {React.ReactNode} footer
 * Optional modal footer content, usually buttons or actions.
 *
 * @param {"sm" | "md" | "lg" | "xl" | "full"} [size="md"]
 * Controls the modal width.
 *
 * @param {"center" | "left" | "right" | "top" | "bottom"} [placement="center"]
 * Controls where the modal appears on the screen.
 *
 * @param {"default" | "inverse"} [variant="default"]
 * Controls the modal visual style.
 *
 * @param {boolean} [closeOnOverlayClick=true]
 * Allows closing the modal by clicking the overlay.
 *
 * @param {boolean} [showCloseButton=true]
 * Controls whether the close button is visible.
 *
 * @param {string} [className=""]
 * Additional custom class names.
 */

import { useEffect, useId, useRef } from "react";
import { LuX } from "react-icons/lu";
import "./Modal.css";

const Modal = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
  placement = "center",
  variant = "default",
  closeOnOverlayClick = true,
  showCloseButton = true,
  className = "",
  ...props
}) => {
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef(null);

  const overlayClasses = [
    "acc-modal-overlay",
    `acc-modal-overlay--${placement}`,
  ]
    .filter(Boolean)
    .join(" ");

  const modalClasses = [
    "acc-modal",
    `acc-modal--${size}`,
    `acc-modal--${variant}`,
    `acc-modal--placement-${placement}`,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && onClose) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    const focusTimer = window.setTimeout(() => {
      dialogRef.current?.focus();
    }, 0);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      window.clearTimeout(focusTimer);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const handleOverlayClick = (event) => {
    if (
      closeOnOverlayClick &&
      event.target === event.currentTarget &&
      onClose
    ) {
      onClose();
    }
  };

  return (
    <div className={overlayClasses} onMouseDown={handleOverlayClick}>
      <div
        ref={dialogRef}
        className={modalClasses}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        {...props}
      >
        {(title || description || showCloseButton) && (
          <div className="acc-modal__header">
            <div className="acc-modal__heading l-stack l-stack--xs">
              {title && (
                <h2 id={titleId} className="acc-modal__title">
                  {title}
                </h2>
              )}

              {description && (
                <p id={descriptionId} className="acc-modal__description">
                  {description}
                </p>
              )}
            </div>

            {showCloseButton && (
              <button
                type="button"
                className="acc-modal__close"
                aria-label="Close modal"
                onClick={onClose}
              >
                <LuX />
              </button>
            )}
          </div>
        )}

        <div className="acc-modal__body">{children}</div>

        {footer && <div className="acc-modal__footer">{footer}</div>}
      </div>
    </div>
  );
};

export default Modal;